import { useNavigate } from "@tanstack/react-router";
import {
  supportColors,
  supportPriorities,
  supportPriorityWords,
  supportUrgencies,
  supportUrgencyWords,
  type AdminSupportCard,
  type AdminSupportStaff,
  type AdminSupportTag,
  type SupportColor,
  type SupportKind,
  type SupportPriority,
} from "@teamna/shared";
import { useState } from "react";
import { Select, SwatchPicker, Tabs, type BadgeTone } from "@teamna/ui/components/index.ts";

/**
 * Pieces the support screens share: the colour palette of columns and labels, the colour picker,
 * a label chip, the team member initials, the filters of the board and the list, and the tabs
 * between the board, the list and the settings.
 */

/** The kinds in the Teamna team's words. The sender reads other words (`supportStateWords`). */
export const KIND_WORDS: Record<SupportKind, string> = {
  open: "Open",
  fix_pending: "Fix pending",
  waiting_customer: "Waiting for the sender",
  closed: "Resolved",
};

export const PRIORITY_TONES: Record<SupportPriority, BadgeTone> = { none: "quiet", low: "quiet", medium: "quiet", high: "late", urgent: "late" };

/**
 * The colour of a column or a label. CSS colour values, drawn as a stripe or a dot only, never
 * under text, so every colour keeps the text contrast of the page.
 */
export const SUPPORT_COLORS: Record<SupportColor, string> = {
  slate: "#64748b",
  // The brand blue. The accent token is navy in Control Center, too close to grey in a swatch.
  blue: "#326CC5",
  teal: "#0f766e",
  green: "var(--color-confirmed)",
  gold: "var(--color-late-gold)",
  red: "var(--color-cancelled)",
  purple: "#6d28d9",
};

export const SUPPORT_COLOR_WORDS: Record<SupportColor, string> = {
  slate: "Grey",
  blue: "Blue",
  teal: "Teal",
  green: "Green",
  gold: "Gold",
  red: "Red",
  purple: "Purple",
};

export function ColorDot({ color, size = 10 }: { color: SupportColor; size?: number }) {
  return <span aria-hidden="true" className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: SUPPORT_COLORS[color] }} />;
}

/** A label on a ticket: a dot in its colour and the name. */
export function TagChip({ tag }: { tag: AdminSupportTag }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-line bg-paper px-2 py-0.5 text-[12px] font-semibold text-ink">
      <ColorDot color={tag.color} size={8} />
      <span dir="auto" className="truncate">
        {tag.name}
      </span>
    </span>
  );
}

export function TagChips({ tags }: { tags: AdminSupportTag[] }) {
  if (tags.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Labels">
      {tags.map((tag) => (
        <li key={tag.id} className="min-w-0">
          <TagChip tag={tag} />
        </li>
      ))}
    </ul>
  );
}

const SWATCHES = supportColors.map((color) => ({ value: color, label: SUPPORT_COLOR_WORDS[color], color: SUPPORT_COLORS[color] }));

/** The colour picker of a column or a label: the support palette in a `SwatchPicker`. */
export function ColorPicker(props: { label: string; value: SupportColor; onChange: (color: SupportColor) => void; disabled?: boolean }) {
  return <SwatchPicker options={SWATCHES} {...props} />;
}

/** The name of a team member, or the part of the address before the @. */
export function staffName(staff: AdminSupportStaff[], email: string): string {
  return staff.find((member) => member.email === email)?.name ?? email.split("@")[0] ?? email;
}

function initials(name: string): string {
  const words = name.trim().split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}

/** The team member on a ticket, as initials in a circle; a screen reader reads the full name. */
export function AssigneeBadge({ name }: { name: string }) {
  return (
    <span title={name} className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-sunken text-[11px] font-semibold text-ink">
      <span aria-hidden="true">{initials(name)}</span>
      <span className="sr-only">Assigned to {name}</span>
    </span>
  );
}

// --- filters --------------------------------------------------------------------------------

const ALL = "all";
export const NOBODY = "nobody";
export const ME = "me";
export const NO_LABEL = "none";

/** The filters the board and the list share (spec section 6.1). "all" is no filter. */
export type TeamFilters = { assignee: string; label: string; priority: string; urgency: string; demo: "all" | "hide" | "only" };

export const NO_FILTERS: TeamFilters = { assignee: ALL, label: ALL, priority: ALL, urgency: ALL, demo: "all" };

export function filtering(filters: TeamFilters): boolean {
  return Object.entries(filters).some(([, value]) => value !== ALL);
}

/**
 * The filters with every value that no longer has an option put back to "all": a label archived or
 * a team member gone since the filters were stored (internal review F1.3 of the team tools). A
 * filter the menu cannot show must never hide tickets.
 */
export function liveFilters(filters: TeamFilters, tags: AdminSupportTag[], staff: AdminSupportStaff[]): TeamFilters {
  const labelKnown = filters.label === ALL || filters.label === NO_LABEL || tags.some((tag) => tag.id === filters.label);
  const assigneeKnown = [ALL, ME, NOBODY].includes(filters.assignee) || staff.some((member) => member.email === filters.assignee);
  return { ...filters, label: labelKnown ? filters.label : ALL, assignee: assigneeKnown ? filters.assignee : ALL };
}

export function matchesFilters(card: AdminSupportCard, filters: TeamFilters, myEmail: string | null): boolean {
  // "Me" before the session has loaded matches nothing, never the tickets of nobody.
  const assignee =
    filters.assignee === ALL ||
    (filters.assignee === NOBODY
      ? card.assigneeStaffEmail === null
      : filters.assignee === ME
        ? myEmail !== null && card.assigneeStaffEmail === myEmail
        : card.assigneeStaffEmail === filters.assignee);
  return (
    assignee &&
    (filters.label === ALL || (filters.label === NO_LABEL ? card.tags.length === 0 : card.tags.some((tag) => tag.id === filters.label))) &&
    (filters.priority === ALL || card.priority === filters.priority) &&
    (filters.urgency === ALL || card.urgency === filters.urgency) &&
    (filters.demo === "all" || (filters.demo === "only") === card.business.demo)
  );
}

/**
 * The filters of one screen, kept for the browser tab, so a team member who opens a ticket and
 * comes back finds the board as they left it. Storage can be off (a private window): then the
 * filters live only while the page is open.
 */
export function useStoredFilters(key: string): [TeamFilters, (next: TeamFilters) => void] {
  const storageKey = `teamna-cc-support-filters-${key}`;
  const [filters, setFilters] = useState<TeamFilters>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? { ...NO_FILTERS, ...(JSON.parse(stored) as Partial<TeamFilters>) } : NO_FILTERS;
    } catch {
      return NO_FILTERS;
    }
  });
  const update = (next: TeamFilters) => {
    setFilters(next);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // No storage: the filters still work for this page.
    }
  };
  return [filters, update];
}

/** The filter menus. They sit in a `Toolbar` on the list and in a row above the board. */
export function TeamFilterSelects({
  filters,
  onChange,
  tags,
  staff,
}: {
  filters: TeamFilters;
  onChange: (next: TeamFilters) => void;
  tags: AdminSupportTag[];
  staff: AdminSupportStaff[];
}) {
  const set = (patch: Partial<TeamFilters>) => onChange({ ...filters, ...patch });
  return (
    <>
      <Select label="Filter by team member" value={filters.assignee} onChange={(event) => set({ assignee: event.target.value })} className="sm:max-w-[200px]">
        <option value={ALL}>Anyone</option>
        <option value={ME}>Me</option>
        <option value={NOBODY}>Nobody</option>
        {staff.map((member) => (
          <option key={member.email} value={member.email}>
            {member.name ?? member.email}
          </option>
        ))}
      </Select>
      <Select label="Filter by label" value={filters.label} onChange={(event) => set({ label: event.target.value })} className="sm:max-w-[200px]">
        <option value={ALL}>All labels</option>
        <option value={NO_LABEL}>No label</option>
        {tags.map((tag) => (
          <option key={tag.id} value={tag.id}>
            {tag.name}
          </option>
        ))}
      </Select>
      <Select label="Filter by priority" value={filters.priority} onChange={(event) => set({ priority: event.target.value })} className="sm:max-w-[180px]">
        <option value={ALL}>Any priority</option>
        {supportPriorities.map((value) => (
          <option key={value} value={value}>
            {supportPriorityWords[value]}
          </option>
        ))}
      </Select>
      <Select label="Filter by urgency" value={filters.urgency} onChange={(event) => set({ urgency: event.target.value })} className="sm:max-w-[220px]">
        <option value={ALL}>Any urgency</option>
        {supportUrgencies.map((value) => (
          <option key={value} value={value}>
            {supportUrgencyWords[value]}
          </option>
        ))}
      </Select>
      <Select
        label="Filter demo clubs"
        value={filters.demo}
        onChange={(event) => set({ demo: event.target.value as TeamFilters["demo"] })}
        className="sm:max-w-[200px]"
      >
        <option value="all">With demo clubs</option>
        <option value="hide">No demo clubs</option>
        <option value="only">Only demo clubs</option>
      </Select>
    </>
  );
}

export type SupportView = "board" | "list" | "settings";

const VIEW_PATHS: Record<SupportView, string> = { board: "/support", list: "/support/list", settings: "/support/settings" };

/** Board, List and Settings, under the Support header. Each tab is its own route. */
export function SupportTabs({ current }: { current: SupportView }) {
  const navigate = useNavigate();
  return (
    <Tabs
      label="Support views"
      selectedId={current}
      onSelect={(id) => void navigate({ to: VIEW_PATHS[id as SupportView] })}
      tabs={[
        { id: "board", label: "Board" },
        { id: "list", label: "List" },
        { id: "settings", label: "Settings" },
      ]}
    />
  );
}
